import { DateTimeSuggestion } from "./DateTimeSuggestion";
import { DateTime } from "luxon";
import { getDayAsNumber, getStartOfWeek, getStartOfWeekend, SpecialDateEnum } from "./utils";
import * as parsing from "./parsing";
import { capitalize } from "lodash-comms";
import { isDefined } from "../predicates";
import { UnreachableCaseError } from "../errors";

export type CommonSuggestionFn = (props: CommonSuggestionProps) => DateTimeSuggestion;

export type CommonSuggestionProps = {
  /**
   * - "SOP" - Start of Period
   * - "EOP" - End of Period
   */
  defaultTimeForSuggestion: "SOP" | "EOP";
  weekStartsOn: "su" | "mo";
  weekendStartsOn: "sa";
};

export const DAYS_OF_WEEK = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"] as const;

export const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

export const DAY_PERIODS = ["day", "week", "month", "year"] as const;

export const TIME_PERIODS = ["minute", "hour"] as const;

export const PERIODS = [...TIME_PERIODS, ...DAY_PERIODS] as const;

/**
 * Examples of valid reminder times:
 *
 * - 9am
 *
 * - 9am on Monday
 * - 9am on Tuesday
 * - 9am on Wednesday
 * - 9am on Thursday
 * - 9am on Friday
 * - 9am on Saturday
 * - 9am on Sunday
 *
 * - 9am on January 3rd
 * - 9am on February 3rd
 * - 9am on March 3rd
 * - 9am on April 3rd
 * - 9am on May 3rd
 * - 9am on June 3rd
 * - 9am on July 3rd
 * - 9am on Auguest 3rd
 * - 9am on September 3rd
 * - 9am on October 3
 * - 9am on November 3
 * - 9am on December 3
 *
 * - 9 January at 3am
 * - 9 February at 3am
 * - 9 March at 3am
 * - 9 April at 3am
 * - 9 May at 3am
 * - 9 June at 3am
 * - 9 July at 3am
 * - 9 Auguest at 3am
 * - 9 September at 3am
 * - 9 October at 3am
 * - 9 November at 3am
 * - 9 December at 3am
 *
 * - 9th of January at 3am
 * - 9th of February at 3am
 * - 9th of March at 3am
 * - 9th of April at 3am
 * - 9th of May at 3am
 * - 9th of June at 3am
 * - 9th of July at 3am
 * - 9th of Auguest at 3am
 * - 9th of September at 3am
 * - 9th of October at 3am
 * - 9th of November at 3am
 * - 9th of December at 3am
 *
 * - m/d
 * - m/d/yy
 * - 9am on mm/dd/yy
 * - 9 on mm/dd/yy
 * - mm/dd/yy at 9
 * - mm/dd/yy at 9am
 *
 * - today at 1pm
 * - today at midnight
 * - today at noon
 *
 * - in a minute
 * - in an hour
 * - in a day
 * - in a week
 * - in a month
 * - in a quarter
 * - in a year
 *
 * - in 1 minute
 * - in 1 hour
 * - in 1 day
 * - in 1 week
 * - in 1 month
 * - in 1 quarter
 * - in 1 year
 *
 * - in 2 minutes
 * - in 2 hours
 * - in 2 days
 * - in 2 weeks
 * - in 2 months
 * - in 2 quarters
 * - in 2 years

 * - in 2 minutes on weekday
 * - in 2 hours on weekday
 * - in 2 days on weekday
 * - in 2 weeks on weekday
 * - in 2 months on weekday
 * - in 2 quarters on weekday
 * - in 2 years on weekday

 * - on Monday at 9am
 * - on Tuesday at 9am
 * - on Wednesday at 9am
 * - on Thursday at 9am
 * - on Friday at 9am
 * - on Saturday at 9am
 * - on Sunday at 9am
 *
 * - on Monday morning
 * - on Monday afternoon
 * - on Monday evening
 * - on Monday night
 * - on Monday morning
 * - on Monday at noon
 * - on Monday morning at 7am
 * - on Monday afternoon at 1pm
 * - on Monday evening at 8pm
 * - on Monday night at 8pm
 * 
 * Examples of valid reminder times which we
 * _don't_ need to parse. Instead, we'll just hardcode
 * suggestions for these times in and let the fuzzy
 * matcher filter them out.
 *
 * - next week
 * - next weekend
 * - next month
 * - next quarter
 * - next year
 * - next Monday
 * - next Tuesday
 * - next Wednesday
 * - next Thursday
 * - next Friday
 * - next Saturday
 * - next Sunday
 * - next January
 * - next February
 * - next March
 * - next April
 * - next May
 * - next June
 * - next July
 * - next August
 * - next September
 * - next October
 * - next November
 * - next December
 *
 * - this morning
 * - this afternoon
 * - this evening
 * - this night
 *
 * - this weekend
 * - this Monday
 * - this Tuesday
 * - this Wednesday
 * - this Thursday
 * - this Friday
 * - this Saturday
 * - this Sunday
 * - this January
 * - this February
 * - this March
 * - this April
 * - this May
 * - this June
 * - this July
 * - this August
 * - this September
 * - this October
 * - this November
 * - this December
 *
 * - today
 * - tomorrow
 * - someday
 * - never
 * - forever
 */

// Note, the order of these suggestions can affect the order for these
// suggestions in the UI. This means that these suggestions should be ordered
// in the way that we'd want them to be displayed in the UI.
//
// With this in mind, in general suggestions should be ordered from simpler to
// more complex and from most common to least common.
export const commonSuggestions = {
  tomorrow: (props) => {
    return new DateTimeSuggestion({
      id: "tomorrow",
      template: `tomorrow`,
      keywords: [`tomorrow at`],
      valueType: "date",
      getDate: (now) => setDefaultTimeForDateSuggestion(props.defaultTimeForSuggestion, now.plus({ day: 1 })),
    });
  },
  // We choose to interpret "next week" as the next "weekStartsOn" day. If it's currently the
  // start of a week, then "next week" will be the next "weekStartsOn" day.
  nextWeek: (props) => {
    return new DateTimeSuggestion({
      id: "nextWeek",
      template: `next week`,
      valueType: "week",
      getDate: (now) => {
        const startOfWeek = getStartOfWeek(props.weekStartsOn, now).plus({ weeks: 1 });

        switch (props.defaultTimeForSuggestion) {
          case "SOP": {
            return startOfWeek;
          }
          case "EOP": {
            return startOfWeek.plus({ days: 6 }).endOf("day");
          }
          default: {
            throw new UnreachableCaseError(
              props.defaultTimeForSuggestion,
              `[nextWeek] invalid defaultTimeForSuggestion: ${props.defaultTimeForSuggestion}`,
            );
          }
        }
      },
    });
  },
  nextWeekend: (props) => {
    return new DateTimeSuggestion({
      id: "nextWeekend",
      template: `next weekend`,
      valueType: "date",
      getDate: (now) => {
        const startOfNextWeekend = getStartOfWeekend(props.weekendStartsOn, now).plus({ weeks: 1 });

        switch (props.defaultTimeForSuggestion) {
          case "SOP": {
            return startOfNextWeekend;
          }
          case "EOP": {
            return startOfNextWeekend.plus({ days: 1 }).endOf("day");
          }
          default: {
            throw new UnreachableCaseError(
              props.defaultTimeForSuggestion,
              `[nextWeekend] invalid defaultTimeForSuggestion: ${props.defaultTimeForSuggestion}`,
            );
          }
        }
      },
    });
  },
  nextMonth: (props) => {
    return new DateTimeSuggestion({
      id: "nextMonth",
      template: `next month`,
      valueType: "month",
      getDate: (now) => {
        const startOfNextMonth = now.plus({ months: 1 }).startOf("month");

        switch (props.defaultTimeForSuggestion) {
          case "SOP": {
            return startOfNextMonth;
          }
          case "EOP": {
            return startOfNextMonth.endOf("month");
          }
          default: {
            throw new UnreachableCaseError(
              props.defaultTimeForSuggestion,
              `[nextMonth] invalid defaultTimeForSuggestion: ${props.defaultTimeForSuggestion}`,
            );
          }
        }
      },
    });
  },
  nextYear: (props) => {
    return new DateTimeSuggestion({
      id: "nextYear",
      template: `next year`,
      valueType: "year",
      getDate: (now) => {
        const startOfNextYear = now.plus({ years: 1 }).startOf("year");

        switch (props.defaultTimeForSuggestion) {
          case "SOP": {
            return startOfNextYear;
          }
          case "EOP": {
            return startOfNextYear.endOf("year");
          }
          default: {
            throw new UnreachableCaseError(
              props.defaultTimeForSuggestion,
              `[nextYear] invalid defaultTimeForSuggestion: ${props.defaultTimeForSuggestion}`,
            );
          }
        }
      },
    });
  },
  never: (props) => {
    return new DateTimeSuggestion({
      id: "never",
      template: `never`,
      valueType: null,
      getDate: () => SpecialDateEnum.SOMEDAY,
    });
  },
  tonight: (props) => {
    return new DateTimeSuggestion({
      id: "tonight",
      template: `tonight`,
      valueType: "datetime",
      getDate: (now) => now.set({ hour: 20 }).startOf("hour"),
    });
  },
  eod: (props) => {
    return new DateTimeSuggestion({
      id: "eod",
      template: `end of day`,
      keywords: ["eod", "end of today"],
      valueType: "date",
      getDate: (now) => now.endOf("day"),
    });
  },
  today: (props) => {
    return new DateTimeSuggestion({
      id: "today",
      template: `today`,
      keywords: [`today at`],
      valueType: "date",
      getDate: (now) => setDefaultTimeForDateSuggestion(props.defaultTimeForSuggestion, now),
    });
  },

  thisWeek: (props) => {
    return new DateTimeSuggestion({
      id: "thisWeek",
      template: `this week`,
      valueType: "week",
      getDate: (now) => {
        const startOfWeek = getStartOfWeek(props.weekStartsOn, now);

        switch (props.defaultTimeForSuggestion) {
          case "SOP": {
            return startOfWeek;
          }
          case "EOP": {
            return startOfWeek.plus({ days: 6 }).endOf("day");
          }
          default: {
            throw new UnreachableCaseError(
              props.defaultTimeForSuggestion,
              `[thisWeek] invalid defaultTimeForSuggestion: ${props.defaultTimeForSuggestion}`,
            );
          }
        }
      },
    });
  },

  thisWeekend: (props) => {
    return new DateTimeSuggestion({
      id: "thisWeekend",
      template: `this weekend`,
      valueType: "week",
      getDate: (now) => {
        const startOfWeekend = getStartOfWeekend(props.weekendStartsOn, now);

        switch (props.defaultTimeForSuggestion) {
          case "SOP": {
            return startOfWeekend;
          }
          case "EOP": {
            return startOfWeekend.plus({ days: 1 }).endOf("day");
          }
          default: {
            throw new UnreachableCaseError(
              props.defaultTimeForSuggestion,
              `[thisWeekend] invalid defaultTimeForSuggestion: ${props.defaultTimeForSuggestion}`,
            );
          }
        }
      },
    });
  },

  thisMonth: (props) => {
    return new DateTimeSuggestion({
      id: "thisMonth",
      template: `this month`,
      valueType: "month",
      getDate: (now) => {
        const startOfMonth = now.startOf("month");

        switch (props.defaultTimeForSuggestion) {
          case "SOP": {
            return startOfMonth;
          }
          case "EOP": {
            return startOfMonth.endOf("month");
          }
          default: {
            throw new UnreachableCaseError(
              props.defaultTimeForSuggestion,
              `[thisMonth] invalid defaultTimeForSuggestion: ${props.defaultTimeForSuggestion}`,
            );
          }
        }
      },
    });
  },

  thisYear: (props) => {
    return new DateTimeSuggestion({
      id: "thisYear",
      template: `this year`,
      valueType: "year",
      getDate: (now) => {
        const startOfYear = now.startOf("year");

        switch (props.defaultTimeForSuggestion) {
          case "SOP": {
            return startOfYear;
          }
          case "EOP": {
            return startOfYear.endOf("year");
          }
          default: {
            throw new UnreachableCaseError(
              props.defaultTimeForSuggestion,
              `[thisYear] invalid defaultTimeForSuggestion: ${props.defaultTimeForSuggestion}`,
            );
          }
        }
      },
    });
  },

  // on Monday
  ...getTypedSuggestionObject(
    DAYS_OF_WEEK,
    (d) => `on${d}`,
    (id, d) => (props) =>
      new DateTimeSuggestion({
        id,
        template: `on ${d}`,
        keywords: [`this ${d}`, `on ${d} at`, `${d} at`],
        valueType: "date",
        getDate: DateTimeSuggestion.defaultGetDateFn((date) =>
          setDefaultTimeForDateSuggestion(props.defaultTimeForSuggestion, date),
        ),
      }),
  ),

  // next Monday
  // We choose to interpret "next week" as the next "weekStartsOn" day. So "next [Day]" is
  // interpreted as, "move to the start of the next week then move forward to the target day".
  ...getTypedSuggestionObject(
    DAYS_OF_WEEK,
    (d) => `next${d}`,
    (id, d) => (props) =>
      new DateTimeSuggestion({
        id,
        template: `next ${d}`,
        keywords: [`next ${d} at`],
        valueType: "date",
        getDate: (now) => {
          // Get the day of the week we're targeting
          const targetDayNumber = getDayAsNumber(d.toLowerCase() as Lowercase<typeof d>);

          // Get the weekday number for the configured first day of the week
          const weekStartDay = getDayAsNumber(props.weekStartsOn);

          // Find the current week's start day
          let weekStart = now.set({ weekday: weekStartDay }).startOf("day");

          // If we're already past the week start, move to next week's start
          if (weekStart <= now.startOf("day")) {
            weekStart = weekStart.plus({ weeks: 1 });
          }

          // Set to the specific day of the week within that week
          const targetDate = weekStart.set({ weekday: targetDayNumber });

          return setDefaultTimeForDateSuggestion(props.defaultTimeForSuggestion, targetDate);
        },
      }),
  ),

  aMinuteFromNow: (props) => {
    return new DateTimeSuggestion({
      id: "aMinuteFromNow",
      template: `a minute from now`,
      keywords: [`in a minute`],
      valueType: "datetime",
      getDate: DateTimeSuggestion.defaultGetDateFn((date) =>
        setDefaultTimeForDateSuggestion(props.defaultTimeForSuggestion, date),
      ),
    });
  },
  anHourFromNow: (props) => {
    return new DateTimeSuggestion({
      id: "anHourFromNow",
      template: `an hour from now`,
      keywords: [`in an hour`],
      valueType: "datetime",
      getDate: DateTimeSuggestion.defaultGetDateFn((date) =>
        setDefaultTimeForDateSuggestion(props.defaultTimeForSuggestion, date),
      ),
    });
  },
  aDayFromNow: (props) => {
    return new DateTimeSuggestion({
      id: "aDayFromNow",
      template: `a day from now`,
      keywords: [`in a day`],
      valueType: "datetime",
      getDate: DateTimeSuggestion.defaultGetDateFn((date) =>
        setDefaultTimeForDateSuggestion(props.defaultTimeForSuggestion, date),
      ),
    });
  },
  aWeekFromNow: (props) => {
    return new DateTimeSuggestion({
      id: "aWeekFromNow",
      template: `a week from now`,
      keywords: [`in a week`],
      valueType: "date",
      getDate: DateTimeSuggestion.defaultGetDateFn((date) =>
        setDefaultTimeForDateSuggestion(props.defaultTimeForSuggestion, date),
      ),
    });
  },

  aMonthFromNow: (props) => {
    return new DateTimeSuggestion({
      id: "aMonthFromNow",
      template: `a month from now`,
      keywords: [`in a month`],
      valueType: "date",
      getDate: DateTimeSuggestion.defaultGetDateFn((date) =>
        setDefaultTimeForDateSuggestion(props.defaultTimeForSuggestion, date),
      ),
    });
  },

  // 10 minutes from now
  ...getTypedSuggestionObject(
    PERIODS,
    (p) => `x${capitalize(p)}sFromNow`,
    (id, p) => (props) =>
      new DateTimeSuggestion({
        id,
        template: `{{NUMBER}} ${p}(s) from now`,
        valueType: p === "minute" || p === "hour" ? "datetime" : "date",
        updateFn({ value }) {
          if (value === "1") {
            this.keywords = [`in 1 ${p}`, `1 ${p} from now at`];
            this.content = `1 ${p} from now`;
            this.contentVariables.amount = 1;

            if (p === "day") this.valueType = "datetime";
          } else {
            this.keywords = [`in ${value} ${p}s`, `${value} ${p}s from now at`];
            this.content = `${value} ${p}s from now`;
            this.contentVariables.amount = parseInt(value, 10);

            if (p === "day") this.valueType = "date";
          }
        },
        finalizeUpdates() {
          if (Number.isInteger(this.contentVariables.amount)) return;
          this.update("{{NUMBER}}", "1");
        },
        getDate: DateTimeSuggestion.defaultGetDateFn((date) =>
          setDefaultTimeForDateSuggestion(props.defaultTimeForSuggestion, date),
        ),
      }),
  ),

  // 10 days from now at 8
  ...getTypedSuggestionObject(
    DAY_PERIODS,
    (p) => `x${capitalize(p)}sFromNowAtNumber`,
    (id, p) => (props) =>
      new DateTimeSuggestion({
        id,
        template: `{{NUMBER}} ${p}(s) from now at {{NUMBER}}`,
        valueType: "datetime",
        updateFn({ content, token, value }) {
          if (tokenCount(content, "{{NUMBER}}") === 2) {
            this.content = content.replace(token, value);
            this.contentVariables.amount = parseInt(value, 10);
          } else {
            const amount = this.contentVariables.amount;
            const timeString = parsing.normalizeNumberAsTimeFromInput(value);
            this.contentVariables.time = parsing.parseNormalizedTime(timeString);

            if (!Number.isInteger(amount)) {
              throw new Error(`[${id}] [updateFn] invalid amount: ${amount}`);
            }

            if (amount === 1) {
              this.keywords = [`in 1 ${p} at ${timeString}`];
              this.content = `1 ${p} from now at ${timeString}`;
            } else {
              this.keywords = [`in ${amount} ${p}s at ${timeString}`];
              this.content = `${amount} ${p}s from now at ${timeString}`;
            }
          }
        },
        finalizeUpdates() {
          if (Number.isInteger(this.contentVariables.amount) && this.contentVariables.time) {
            return;
          }

          this.disabled = true;
        },
        getDate: DateTimeSuggestion.defaultGetDateFn((date) =>
          setDefaultTimeForDateSuggestion(props.defaultTimeForSuggestion, date),
        ),
      }),
  ),

  // 10 days from now at 8am
  ...getTypedSuggestionObject(
    DAY_PERIODS,
    (p) => `x${capitalize(p)}sFromNowAtTime`,
    (id, p) => (props) =>
      new DateTimeSuggestion({
        id,
        template: `{{NUMBER}} ${p}(s) from now at {{TIME}}`,
        keywords: [`in {{NUMBER}} ${p}(s) at {{TIME}}`],
        valueType: "datetime",
        updateFn({ content, token, value }) {
          // The order that of the tokens passed to this updateFn is not guaranteed.
          if (token === "{{NUMBER}}") {
            const amount = parseInt(value, 10);
            this.contentVariables.amount = amount;
            this.content = content.replace("{{NUMBER}}", amount.toString());
            this.keywords = this.keywords.map((k) => k.replace("{{NUMBER}}", amount.toString()));

            if (amount === 1) {
              this.content = this.content.replace(`${p}(s)`, p);
              this.keywords = this.keywords.map((k) => k.replace(`${p}(s)`, p));
            } else {
              this.content = this.content.replace(`${p}(s)`, `${p}s`);
              this.keywords = this.keywords.map((k) => k.replace(`${p}(s)`, `${p}s`));
            }
          } else {
            const timeString = parsing.normalizeTimeFromInput(value);
            this.contentVariables.time = parsing.parseNormalizedTime(timeString);
            this.content = content.replace("{{TIME}}", timeString);
            this.keywords = this.keywords.map((k) => k.replace("{{TIME}}", timeString));
          }

          // console.log("updateFn", token, value, content, this.contentVariables, this.content, this.keywords);
        },
        finalizeUpdates() {
          if (Number.isInteger(this.contentVariables.amount) && this.contentVariables.time) {
            return;
          }

          this.disabled = true;
        },
        getDate: DateTimeSuggestion.defaultGetDateFn((date) =>
          setDefaultTimeForDateSuggestion(props.defaultTimeForSuggestion, date),
        ),
      }),
  ),

  // today 9
  todayAtNumber: (props) => {
    return new DateTimeSuggestion({
      id: "todayAtNumber",
      template: `today at {{NUMBER}}`,
      valueType: "datetime",
      updateFn({ value }) {
        this.keywords = [`${parsing.normalizeNumberAsTimeFromInput(value)} today`];
        this.content = `today at ${parsing.normalizeNumberAsTimeFromInput(value)}`;
        this.contentVariables.time = parsing.parseNormalizedTime(parsing.normalizeNumberAsTimeFromInput(value));
      },
      getDate: DateTimeSuggestion.defaultGetDateFn((date) =>
        setDefaultTimeForDateSuggestion(props.defaultTimeForSuggestion, date),
      ),
    });
  },

  // today 9am
  todayAtTime: (props) => {
    return new DateTimeSuggestion({
      id: "todayAtTime",
      template: `today at {{TIME}}`,
      valueType: "datetime",
      updateFn({ value }) {
        this.keywords = [`${value} today`, `${value.replace(":", " ")} today`];
        this.content = `today at ${value}`;
        this.contentVariables.time = parsing.parseNormalizedTime(value);
      },
      getDate: DateTimeSuggestion.defaultGetDateFn((date) =>
        setDefaultTimeForDateSuggestion(props.defaultTimeForSuggestion, date),
      ),
    });
  },

  // tomorrow 9
  tomorrowAtNumber: (props) => {
    return new DateTimeSuggestion({
      id: "tomorrowAtNumber",
      template: `tomorrow at {{NUMBER}}`,
      valueType: "datetime",
      updateFn({ value }) {
        this.keywords = [`${parsing.normalizeNumberAsTimeFromInput(value)} tomorrow`];
        this.content = `tomorrow at ${parsing.normalizeNumberAsTimeFromInput(value)}`;
        this.contentVariables.time = parsing.parseNormalizedTime(parsing.normalizeNumberAsTimeFromInput(value));
      },
      getDate: DateTimeSuggestion.defaultGetDateFn((date) =>
        setDefaultTimeForDateSuggestion(props.defaultTimeForSuggestion, date),
      ),
    });
  },

  // tomorrow 9am
  tomorrowAtTime: (props) => {
    return new DateTimeSuggestion({
      id: "tomorrowAtTime",
      template: `tomorrow at {{TIME}}`,
      valueType: "datetime",
      updateFn({ value }) {
        this.keywords = [`${value} tomorrow`, `${value.replace(":", " ")} tomorrow`];
        this.content = `tomorrow at ${value}`;
        this.contentVariables.time = parsing.parseNormalizedTime(value);
      },
      getDate: DateTimeSuggestion.defaultGetDateFn((date) =>
        setDefaultTimeForDateSuggestion(props.defaultTimeForSuggestion, date),
      ),
    });
  },

  // on Monday 8
  ...getTypedSuggestionObject(
    DAYS_OF_WEEK,
    (d) => `on${d}AtNumber`,
    (id, d) => (props) =>
      new DateTimeSuggestion({
        id,
        template: `on ${d} at {{NUMBER}}`,
        valueType: "datetime",
        updateFn({ value }) {
          this.content = `on ${d} at ${parsing.normalizeNumberAsTimeFromInput(value)}`;
          this.keywords = [`this ${d} at ${parsing.normalizeNumberAsTimeFromInput(value)}`];
          this.contentVariables.time = parsing.parseNormalizedTime(parsing.normalizeNumberAsTimeFromInput(value));
        },
        getDate(now) {
          const { time } = this.contentVariables;
          if (!time) return null;

          const date = DateTimeSuggestion.parseDate(`on ${d}`, now);
          if (!date) return null;

          const { hours, minutes } = time;
          return date.set({ hour: hours, minute: minutes }).startOf("minute");
        },
      }),
  ),

  // on Monday at 8am
  ...getTypedSuggestionObject(
    DAYS_OF_WEEK,
    (d) => `on${d}AtTime`,
    (id, d) => (props) =>
      new DateTimeSuggestion({
        id,
        template: `on ${d} at {{TIME}}`,
        valueType: "datetime",
        updateFn({ value }) {
          this.content = `on ${d} at ${value}`;
          this.keywords = [`this ${d} at ${value}`];
          this.contentVariables.time = parsing.parseNormalizedTime(value);
        },
        getDate(now) {
          const { time } = this.contentVariables;
          if (!time) return null;

          const date = DateTimeSuggestion.parseDate(`on ${d}`, now);
          if (!date) return null;

          const { hours, minutes } = time;
          return date.set({ hour: hours, minute: minutes }).startOf("minute");
        },
      }),
  ),

  // next Monday at 8
  // We choose to interpret "next week" as the next "weekStartsOn" day. So "next [Day]" is
  // interpreted as, "move to the start of the next week then move forward to the target day".
  ...getTypedSuggestionObject(
    DAYS_OF_WEEK,
    (d) => `next${d}AtNumber`,
    (id, d) => (props) =>
      new DateTimeSuggestion({
        id,
        template: `next ${d} at {{NUMBER}}`,
        valueType: "datetime",
        updateFn({ value }) {
          this.content = `next ${d} at ${parsing.normalizeNumberAsTimeFromInput(value)}`;
          this.contentVariables.time = parsing.parseNormalizedTime(parsing.normalizeNumberAsTimeFromInput(value));
        },
        getDate(now) {
          // Get the day of the week we're targeting
          const targetDayNumber = getDayAsNumber(d.toLowerCase() as Lowercase<typeof d>);

          // Get the weekday number for the configured first day of the week
          const weekStartDay = getDayAsNumber(props.weekStartsOn);

          // Find the current week's start day
          let weekStart = now.set({ weekday: weekStartDay }).startOf("day");

          // If we're already past the week start, move to next week's start
          if (weekStart <= now.startOf("day")) {
            weekStart = weekStart.plus({ weeks: 1 });
          }

          // Set to the specific day of the week within that week
          const targetDate = weekStart.set({ weekday: targetDayNumber });

          const { time } = this.contentVariables;

          if (!time) return null;

          const { hours, minutes } = time;

          return targetDate.set({ hour: hours, minute: minutes }).startOf("minute");
        },
      }),
  ),

  // next Monday at 8am
  // We choose to interpret "next week" as the next "weekStartsOn" day. So "next [Day]" is
  // interpreted as, "move to the start of the next week then move forward to the target day".
  ...getTypedSuggestionObject(
    DAYS_OF_WEEK,
    (d) => `next${d}AtTime`,
    (id, d) => (props) =>
      new DateTimeSuggestion({
        id,
        template: `next ${d} at {{TIME}}`,
        valueType: "datetime",
        updateFn({ value }) {
          this.content = `next ${d} at ${value}`;
          this.contentVariables.time = parsing.parseNormalizedTime(value);
        },
        getDate(now) {
          // Get the day of the week we're targeting
          const targetDayNumber = getDayAsNumber(d.toLowerCase() as Lowercase<typeof d>);

          // Get the weekday number for the configured first day of the week
          const weekStartDay = getDayAsNumber(props.weekStartsOn);

          // Find the current week's start day
          let weekStart = now.set({ weekday: weekStartDay }).startOf("day");

          // If we're already past the week start, move to next week's start
          if (weekStart <= now.startOf("day")) {
            weekStart = weekStart.plus({ weeks: 1 });
          }

          // Set to the specific day of the week within that week
          const targetDate = weekStart.set({ weekday: targetDayNumber });

          const { time } = this.contentVariables;

          if (!time) return null;

          const { hours, minutes } = time;

          return targetDate.set({ hour: hours, minute: minutes }).startOf("minute");
        },
      }),
  ),

  // Next week 9
  // We choose to interpret "next week" as the next "weekStartsOn" day. So "next [Day]" is
  // interpreted as, "move to the start of the next week then move forward to the target day".
  nextWeekAtNumber: (props) => {
    return new DateTimeSuggestion({
      id: "nextWeekAtNumber",
      template: `next week at {{NUMBER}}`,
      valueType: "datetime",
      updateFn({ value }) {
        this.content = `next week at ${parsing.normalizeNumberAsTimeFromInput(value)}`;
        this.contentVariables.time = parsing.parseNormalizedTime(parsing.normalizeNumberAsTimeFromInput(value));
      },
      getDate(now) {
        // Get the weekday number for the first day of the week (Monday or Sunday)
        const weekStartDay = getDayAsNumber(props.weekStartsOn);
        // Find the next occurrence of the week start day
        let weekStart = now.set({ weekday: weekStartDay }).startOf("day");
        // If we're already past the week start, move to next week's start
        if (weekStart <= now.startOf("day")) {
          weekStart = weekStart.plus({ weeks: 1 });
        }

        const { time } = this.contentVariables;

        if (!time) return null;

        const { hours, minutes } = time;

        return weekStart.set({ hour: hours, minute: minutes }).startOf("minute");
      },
    });
  },

  // Next week at 9am
  // We choose to interpret "next week" as the next "weekStartsOn" day. So "next [Day]" is
  // interpreted as, "move to the start of the next week then move forward to the target day".
  nextWeekAtTime: (props) => {
    return new DateTimeSuggestion({
      id: "nextWeekAtTime",
      template: `next week at {{TIME}}`,
      valueType: "datetime",
      updateFn({ value }) {
        this.content = `next week at ${value}`;
        this.contentVariables.time = parsing.parseNormalizedTime(value);
      },
      getDate(now) {
        // Get the weekday number for the first day of the week (Monday or Sunday)
        const weekStartDay = getDayAsNumber(props.weekStartsOn);
        // Find the next occurrence of the week start day
        let weekStart = now.set({ weekday: weekStartDay }).startOf("day");
        // If we're already past the week start, move to next week's start
        if (weekStart <= now.startOf("day")) {
          weekStart = weekStart.plus({ weeks: 1 });
        }

        const { time } = this.contentVariables;

        if (!time) return null;

        const { hours, minutes } = time;

        return weekStart.set({ hour: hours, minute: minutes }).startOf("minute");
      },
    });
  },

  // 9 on Monday
  ...getTypedSuggestionObject(
    DAYS_OF_WEEK,
    (d) => `numberOn${d}`,
    (id, d) => (props) =>
      new DateTimeSuggestion({
        id,
        template: `{{NUMBER}} on ${d}`,
        updateFn({ value }) {
          this.content = `${parsing.normalizeNumberAsTimeFromInput(value)} on ${d}`;
          this.contentVariables.time = parsing.parseNormalizedTime(parsing.normalizeNumberAsTimeFromInput(value));
        },
        valueType: "datetime",
        getDate(now) {
          const { time } = this.contentVariables;
          if (!time) return null;

          const date = DateTimeSuggestion.parseDate(`on ${d}`, now);
          if (!date) return null;

          const { hours, minutes } = time;
          return date.set({ hour: hours, minute: minutes }).startOf("minute");
        },
      }),
  ),

  // 9am on Monday
  ...getTypedSuggestionObject(
    DAYS_OF_WEEK,
    (d) => `timeOn${d}`,
    (id, d) => (props) =>
      new DateTimeSuggestion({
        id,
        template: `{{TIME}} on ${d}`,
        updateFn({ value }) {
          this.content = `${value} on ${d}`;
          this.contentVariables.time = parsing.parseNormalizedTime(value);
        },
        valueType: "datetime",
        getDate(now) {
          const { time } = this.contentVariables;
          if (!time) return null;

          const date = DateTimeSuggestion.parseDate(`on ${d}`, now);
          if (!date) return null;

          const { hours, minutes } = time;
          return date.set({ hour: hours, minute: minutes }).startOf("minute");
        },
      }),
  ),

  // January 9
  ...getTypedSuggestionObject(
    MONTHS,
    (m) => `on${m}Number`,
    (id, m) => (props) =>
      new DateTimeSuggestion({
        id,
        template: `on ${m} {{NUMBER}}`,
        valueType: "date",
        updateFn({ value }) {
          if (parseInt(value, 10) > 31) {
            this.content = null;
          } else {
            this.content = `on ${m} ${numberWithEnding(value)}`;
            this.keywords = [`${m} ${numberWithEnding(value)}`];
            this.contentVariables.dayOfMonth = parseInt(value, 10);
          }
        },
        finalizeUpdates() {
          if (Number.isInteger(this.contentVariables.dayOfMonth)) return;
          this.update("{{NUMBER}}", "1");
        },
        getDate: DateTimeSuggestion.defaultGetDateFn((date) =>
          setDefaultTimeForDateSuggestion(props.defaultTimeForSuggestion, date),
        ),
      }),
  ),

  // 9 January
  ...getTypedSuggestionObject(
    MONTHS,
    (m) => `numberOf${m}`,
    (id, m) => (props) =>
      new DateTimeSuggestion({
        id,
        template: `{{NUMBER}} of ${m}`,
        valueType: "date",
        updateFn({ value }) {
          if (parseInt(value, 10) > 31) {
            this.content = null;
            return;
          }

          this.content = `${numberWithEnding(value)} of ${m}`;
          this.contentVariables.dayOfMonth = parseInt(value, 10);
        },
        getDate: DateTimeSuggestion.defaultGetDateFn((date) =>
          setDefaultTimeForDateSuggestion(props.defaultTimeForSuggestion, date),
        ),
      }),
  ),

  // 9 January 8am
  ...getTypedSuggestionObject(
    MONTHS,
    (m) => `numberOf${m}AtTime`,
    (id, m) => (props) =>
      new DateTimeSuggestion({
        id,
        template: `{{NUMBER}} of ${m} at {{TIME}}`,
        valueType: "datetime",
        updateFn({ content, token, value }) {
          if (token === "{{NUMBER}}") {
            if (parseInt(value, 10) > 31) {
              this.content = null;
              return;
            }

            this.content = content.replace(token, numberWithEnding(value));
            this.contentVariables.dayOfMonth = parseInt(value, 10);
          } else {
            this.content = content.replace(token, value);
            this.contentVariables.time = parsing.parseNormalizedTime(value);
          }
        },
        getDate(now) {
          const { dayOfMonth, time } = this.contentVariables;
          if (!isDefined(dayOfMonth) || !time) return null;

          const date = DateTimeSuggestion.parseDate(`${numberWithEnding(String(dayOfMonth))} of ${m}`, now);
          if (!date) return null;

          const { hours, minutes } = time;
          return date.set({ hour: hours, minute: minutes }).startOf("minute");
        },
      }),
  ),

  // 9am January
  ...getTypedSuggestionObject(
    MONTHS,
    (m) => `timeOn${m}`,
    (id, m) => (props) =>
      new DateTimeSuggestion({
        id,
        template: `{{TIME}} on ${m} 1st`,
        valueType: "datetime",
        updateFn({ value }) {
          this.content = `${value} on ${m} 1st`;
          this.contentVariables.time = parsing.parseNormalizedTime(value);
        },
        getDate(now) {
          const { time } = this.contentVariables;
          if (!time) return null;

          const date = DateTimeSuggestion.parseDate(`on ${m} 1st`, now);
          if (!date) return null;

          const { hours, minutes } = time;
          return date.set({ hour: hours, minute: minutes }).startOf("minute");
        },
      }),
  ),

  // 9am January 3
  ...getTypedSuggestionObject(
    MONTHS,
    (m) => `timeOn${m}Number`,
    (id, m) => (props) =>
      new DateTimeSuggestion({
        id,
        template: `{{TIME}} on ${m} {{NUMBER}}`,
        valueType: "datetime",
        updateFn({ content, token, value }) {
          if (token === "{{NUMBER}}") {
            if (parseInt(value, 10) > 31) {
              this.content = null;
              return;
            }

            this.content = content.replace(token, numberWithEnding(value));
            this.contentVariables.dayOfMonth = parseInt(value, 10);
          } else {
            this.content = content.replace(token, value);
            this.contentVariables.time = parsing.parseNormalizedTime(value);
          }
        },
        getDate(now) {
          const { dayOfMonth, time } = this.contentVariables;
          if (!isDefined(dayOfMonth) || !time) return null;

          const date = DateTimeSuggestion.parseDate(`on ${m} ${numberWithEnding(String(dayOfMonth))}`, now);
          if (!date) return null;

          const { hours, minutes } = time;
          return date.set({ hour: hours, minute: minutes }).startOf("minute");
        },
      }),
  ),

  // 9 January 3
  ...getTypedSuggestionObject(
    MONTHS,
    (m) => `numberOf${m}AtNumber`,
    (id, m) => (props) =>
      new DateTimeSuggestion({
        id,
        template: `{{NUMBER}} of ${m} at {{NUMBER}}`,
        valueType: "datetime",
        updateFn({ content, token, value }) {
          if (content.indexOf(token) === 0) {
            if (parseInt(value, 10) > 31) {
              this.content = null;
              return;
            }

            this.content = content.replace(token, numberWithEnding(value));
            this.contentVariables.dayOfMonth = parseInt(value, 10);
          } else {
            this.content = content.replace(token, parsing.normalizeNumberAsTimeFromInput(value));
            this.contentVariables.time = parsing.parseNormalizedTime(parsing.normalizeNumberAsTimeFromInput(value));
          }
        },
        getDate(now) {
          const { dayOfMonth, time } = this.contentVariables;
          if (!isDefined(dayOfMonth) || !time) return null;

          const date = DateTimeSuggestion.parseDate(`${numberWithEnding(String(dayOfMonth))} of ${m}`, now);
          if (!date) return null;

          const { hours, minutes } = time;
          return date.set({ hour: hours, minute: minutes }).startOf("minute");
        },
      }),
  ),

  // 9 on January 9
  // This differes from `9 Jan 9` in that the first 9 is changed
  // from a date to a time interpretation by the presence of "on"
  ...getTypedSuggestionObject(
    MONTHS,
    (m) => `numberOn${m}Number`,
    (id, m) => (props) =>
      new DateTimeSuggestion({
        id,
        template: `{{NUMBER}} on ${m} {{NUMBER}}`,
        valueType: "datetime",
        updateFn({ content, token, value }) {
          if (content.indexOf(token) === 0) {
            this.content = content.replace(token, parsing.normalizeNumberAsTimeFromInput(value));
            this.contentVariables.time = parsing.parseNormalizedTime(parsing.normalizeNumberAsTimeFromInput(value));
          } else {
            if (parseInt(value, 10) > 31) {
              this.content = null;
              return;
            }

            this.content = content.replace(token, numberWithEnding(value));
            this.contentVariables.dayOfMonth = parseInt(value, 10);
          }
        },
        getDate(now) {
          const { dayOfMonth, time } = this.contentVariables;
          if (!isDefined(dayOfMonth) || !time) return null;

          const date = DateTimeSuggestion.parseDate(`on ${m} ${numberWithEnding(String(dayOfMonth))}`, now);
          if (!date) return null;

          const { hours, minutes } = time;
          return date.set({ hour: hours, minute: minutes }).startOf("minute");
        },
      }),
  ),

  // January 9 9
  ...getTypedSuggestionObject(
    MONTHS,
    (m) => `${m as Lowercase<typeof m>}NumberAtNumber`,
    (id, m) => (props) =>
      new DateTimeSuggestion({
        id,
        template: `${m} {{NUMBER}} at {{NUMBER}}`,
        valueType: "datetime",
        updateFn({ content, token, value }) {
          if (tokenCount(content, token) === 2) {
            if (parseInt(value, 10) > 31) {
              this.content = null;
              return;
            }

            this.content = content.replace(token, numberWithEnding(value));
            this.contentVariables.dayOfMonth = parseInt(value, 10);
          } else {
            this.content = content.replace(token, parsing.normalizeNumberAsTimeFromInput(value));
            this.contentVariables.time = parsing.parseNormalizedTime(parsing.normalizeNumberAsTimeFromInput(value));
          }
        },
        getDate(now) {
          const { dayOfMonth, time } = this.contentVariables;
          if (!isDefined(dayOfMonth) || !time) return null;

          const date = DateTimeSuggestion.parseDate(`${m} ${numberWithEnding(String(dayOfMonth))}`, now);
          if (!date) return null;

          const { hours, minutes } = time;
          return date.set({ hour: hours, minute: minutes }).startOf("minute");
        },
      }),
  ),

  // January 9 9p
  ...getTypedSuggestionObject(
    MONTHS,
    (m) => `${m as Lowercase<typeof m>}NumberAtTime`,
    (id, m) => (props) =>
      new DateTimeSuggestion({
        id,
        template: `${m} {{NUMBER}} at {{TIME}}`,
        valueType: "datetime",
        updateFn({ content, token, value }) {
          if (token === "{{NUMBER}}") {
            if (parseInt(value, 10) > 31) {
              this.content = null;
              return;
            }

            this.content = content.replace(token, numberWithEnding(value));
            this.contentVariables.dayOfMonth = parseInt(value, 10);
          } else {
            this.content = content.replace(token, value);
            this.contentVariables.time = parsing.parseNormalizedTime(value);
          }
        },
        getDate(now) {
          const { dayOfMonth, time } = this.contentVariables;
          if (!isDefined(dayOfMonth) || !time) return null;

          const date = DateTimeSuggestion.parseDate(`${m} ${numberWithEnding(String(dayOfMonth))}`, now);
          if (!date) return null;

          const { hours, minutes } = time;
          return date.set({ hour: hours, minute: minutes }).startOf("minute");
        },
      }),
  ),

  // 1/1/24
  onDate: (props) => {
    return new DateTimeSuggestion({
      id: "onDate",
      template: `on {{DATE}}`,
      valueType: "date",
      updateFn({ content, token, value }) {
        this.content = content.replace(token, value);
        this.keywords = [this.content + " at"];
        this.contentVariables.date = value;
      },
      finalizeUpdates(now) {
        if (!this.contentVariables.date) {
          this.update("{{DATE}}", now.toLocaleString(DateTime.DATE_SHORT));
        }
      },
      getDate: DateTimeSuggestion.defaultGetDateFn((date) =>
        setDefaultTimeForDateSuggestion(props.defaultTimeForSuggestion, date),
      ),
    });
  },

  // 1/1 at 9
  onDateAtNumber: (props) => {
    return new DateTimeSuggestion({
      id: "onDateAtNumber",
      template: `on {{DATE}} at {{NUMBER}}`,
      valueType: "datetime",
      updateFn({ content, token, value }) {
        if (token === "{{NUMBER}}") {
          this.content = content.replace(token, parsing.normalizeNumberAsTimeFromInput(value));
          this.contentVariables.time = parsing.parseNormalizedTime(parsing.normalizeNumberAsTimeFromInput(value));
        } else {
          this.content = content.replace(token, value);
          this.contentVariables.date = value;
        }
      },
      getDate(now) {
        const { date: dateString, time } = this.contentVariables;
        if (!dateString || !time) return null;

        // In testing, chrono-node seems to return the wrong time sometimes. To work around this,
        // we're setting the time ourselves.
        const date = DateTimeSuggestion.parseDate(`on ${dateString}`, now);
        if (!date) return null;

        const { hours, minutes } = time;
        return date.set({ hour: hours, minute: minutes }).startOf("minute");
      },
    });
  },

  // 1/1 at 9am
  onDateAtTime: (props) => {
    return new DateTimeSuggestion({
      id: "onDateAtTime",
      template: `on {{DATE}} at {{TIME}}`,
      valueType: "datetime",
      updateFn({ content, token, value }) {
        if (token === "{{TIME}}") {
          this.content = content.replace(token, value);
          this.contentVariables.time = parsing.parseNormalizedTime(value);
        } else {
          this.content = content.replace(token, value);
          this.contentVariables.date = value;
        }
      },
      getDate(now) {
        const { date: dateString, time } = this.contentVariables;
        if (!dateString || !time) return null;

        // In testing, chrono-node seems to return the wrong time sometimes. To work around this,
        // we're setting the time ourselves.
        const date = DateTimeSuggestion.parseDate(`on ${dateString}`, now);
        if (!date) return null;

        const { hours, minutes } = time;
        return date.set({ hour: hours, minute: minutes }).startOf("minute");
      },
    });
  },

  // 9 on 1/1
  numberOnDate: (props) => {
    return new DateTimeSuggestion({
      id: "numberOnDate",
      template: `{{NUMBER}} on {{DATE}}`,
      valueType: "datetime",
      updateFn({ content, token, value }) {
        if (token === "{{NUMBER}}") {
          this.content = content.replace(token, parsing.normalizeNumberAsTimeFromInput(value));
          this.contentVariables.time = parsing.parseNormalizedTime(parsing.normalizeNumberAsTimeFromInput(value));
        } else {
          this.content = content.replace(token, value);
          this.contentVariables.date = value;
        }
      },
      getDate(now) {
        const { date: dateString, time } = this.contentVariables;
        if (!dateString || !time) return null;

        // In testing, chrono-node seems to return the wrong time sometimes. To work around this,
        // we're setting the time ourselves.
        const date = DateTimeSuggestion.parseDate(`on ${dateString}`, now);
        if (!date) return null;

        const { hours, minutes } = time;
        return date.set({ hour: hours, minute: minutes }).startOf("minute");
      },
    });
  },

  // 9am on 1/1
  timeOnDate: (props) => {
    return new DateTimeSuggestion({
      id: "timeOnDate",
      template: `{{TIME}} on {{DATE}}`,
      valueType: "datetime",
      updateFn({ content, token, value }) {
        if (token === "{{TIME}}") {
          this.content = content.replace(token, value);
          this.contentVariables.time = parsing.parseNormalizedTime(value);
        } else {
          this.content = content.replace(token, value);
          this.contentVariables.date = value;
        }
      },
      getDate(now) {
        const { date: dateString, time } = this.contentVariables;
        if (!dateString || !time) return null;

        // In testing, chrono-node seems to return the wrong time sometimes. To work around this,
        // we're setting the time ourselves.
        const date = DateTimeSuggestion.parseDate(`on ${dateString}`, now);
        if (!date) return null;

        const { hours, minutes } = time;
        return date.set({ hour: hours, minute: minutes }).startOf("minute");
      },
    });
  },

  someday: (props) => {
    return new DateTimeSuggestion({
      id: "someday",
      template: `someday`,
      getDate: () => SpecialDateEnum.SOMEDAY,
      valueType: null,
    });
  },
  forever: (props) => {
    return new DateTimeSuggestion({
      id: "forever",
      template: `forever`,
      getDate: () => SpecialDateEnum.SOMEDAY,
      valueType: null,
    });
  },
} satisfies Record<string, CommonSuggestionFn>;

function setDefaultTimeForDateSuggestion(defaultTime: "8am" | "EOP" | "SOP", date: DateTime) {
  switch (defaultTime) {
    case "8am": {
      return date.set({ hour: 8 }).startOf("hour");
    }
    case "EOP": {
      return date.endOf("day");
    }
    case "SOP": {
      return date.startOf("day");
    }
    default: {
      throw new UnreachableCaseError(defaultTime, `Invalid defaultTimeForSuggestion: ${defaultTime}`);
    }
  }
}

function tokenCount(content: string, token: string) {
  return Array.from(content.matchAll(new RegExp(token, "g"))).length;
}

function numberWithEnding(digit: string) {
  switch (digit) {
    case "11":
    case "12":
    case "13": {
      return `${digit}th`;
    }
  }

  switch (digit.at(-1)) {
    case "1": {
      return `${digit}st`;
    }
    case "2": {
      return `${digit}nd`;
    }
    case "3": {
      return `${digit}rd`;
    }
    default: {
      return `${digit}th`;
    }
  }
}

function getTypedSuggestionObject<T extends string, I extends string>(
  entries: readonly T[],
  getId: (entry: T) => I,
  fn: (id: I, entry: T) => CommonSuggestionFn,
): Record<I, CommonSuggestionFn> {
  return Object.fromEntries(
    entries.map((e) => {
      const id = getId(e);
      return [id, fn(id, e)] as const;
    }),
  ) as Record<I, CommonSuggestionFn>;
}
